import { useEffect, useState } from "react";
import { Link, useSearch } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Container,
  Send,
  Truck,
  Weight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { SkipMark } from "@/components/skip-mark";
import {
  BILLING_KEY,
  describeError,
  isPaymentRequired,
  paymentReason,
  useBillingStatus,
  useLoadsRange,
  useSaveSettings,
  useSendReport,
  useSettings,
  type SendReportResult,
} from "@/lib/api";
import { PaywallCard } from "@/components/billing";
import {
  addDays,
  buildDailyLoadsRows,
  buildWeeklyLoadsRows,
  isoWeekStart,
  shiftReportAnchor,
  TERMINAL_TZ_LABEL,
  todayKey,
  type ReportLoadRow,
  type ReportView,
  type WeeklyLoadsReport,
} from "@/lib/report";

type View = ReportView;

// The report is a pure function of one business day: the day itself is
// the Daily view, and the ISO week containing it is the Weekly view.
// `today` is a prop so that day is injectable (tests pin it) rather than
// being read from the clock deep inside the render; it is the DEFAULT
// anchor, which the prev/next controls then move.
export function Report({ today = todayKey() }: { today?: string }) {
  // In-cab logging is the frequent action, so Daily is the landing view;
  // Weekly is the once-a-week submit.
  const [view, setView] = useState<View>("daily");

  // The business day the report is pointed at. Defaults to today — the
  // driver lands on the current day / current week — and the arrows walk
  // it. Being able to reach LAST week is the point: that is the one
  // dispatch is about to settle, and the one they send.
  const [anchor, setAnchor] = useState<string>(today);

  // One fetch covers the whole settlement week containing the anchor, so
  // toggling views (and stepping a day inside the week) never re-hits
  // the network; stepping out of the week fetches the new one.
  const weekStart = isoWeekStart(anchor);
  const weekEnd = addDays(weekStart, 6);
  const { data, isPending, isError, error } = useLoadsRange(weekStart, weekEnd);

  const loads = data?.loads ?? null;
  const daily = buildDailyLoadsRows(loads, anchor);
  const weekly = buildWeeklyLoadsRows(loads, anchor);

  const needAmount = view === "daily" ? daily.needAmountCount : weekly.needAmountCount;
  const totalLabel = view === "daily" ? daily.totalLabel : weekly.totalLabel;
  const count = view === "daily" ? daily.count : weekly.count;

  const send = useSendReport();
  const [sendOpen, setSendOpen] = useState(false);

  const billing = useBillingStatus();

  // Stripe returns the driver to /report?billing=success. The subscription
  // itself is recorded by the webhook, which this browser never sees — so
  // the only thing to do on arrival is throw away the cached status and ask
  // again, which is what turns the paywall back into a working app.
  //
  // Declared AFTER useBillingStatus on purpose: effects run in declaration
  // order, and invalidating before react-query has subscribed the observer
  // would be a no-op that silently left the paywall up.
  const search = useSearch();
  const qc = useQueryClient();
  const billingParam = new URLSearchParams(search).get("billing");
  useEffect(() => {
    if (billingParam === "success") {
      void qc.invalidateQueries({ queryKey: [BILLING_KEY] });
    }
  }, [billingParam, qc]);

  // A result belongs to the window it was sent for. Moving the window or
  // switching views clears it, so a green "sent" banner can never sit
  // under a different week's numbers.
  function retarget(next: () => void): void {
    send.reset();
    setSendOpen(false);
    next();
  }

  const periodLabel =
    view === "daily"
      ? daily.dayLabel
      : `Week of ${weekly.rangeLabel} · settles ${weekly.settlesLabel}`;

  return (
    <div className="min-h-dvh max-w-3xl mx-auto px-4 py-6 space-y-6">
      <header className="space-y-3">
        <Link
          href="/"
          className="inline-flex items-center gap-1 -ml-1 text-sm text-muted-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          Loads
        </Link>
        <div>
          <div className="flex items-center gap-2.5">
            <SkipMark className="text-[#2f6fed]" />
            <h1 className="text-2xl font-bold tracking-tight">Report</h1>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 -ml-2"
              aria-label={view === "daily" ? "Previous day" : "Previous week"}
              onClick={() => retarget(() => setAnchor((a) => shiftReportAnchor(view, a, -1)))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <p className="flex-1 text-center text-sm text-muted-foreground">
              {periodLabel}
            </p>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 -mr-2"
              aria-label={view === "daily" ? "Next day" : "Next week"}
              onClick={() => retarget(() => setAnchor((a) => shiftReportAnchor(view, a, 1)))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <div
        role="tablist"
        aria-label="Report period"
        className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-muted"
      >
        <PeriodTab
          label="Daily"
          value="daily"
          view={view}
          onSelect={(v) => retarget(() => setView(v))}
        />
        <PeriodTab
          label="Weekly"
          value="weekly"
          view={view}
          onSelect={(v) => retarget(() => setView(v))}
        />
      </div>

      {isPending && (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}

      {isError && (
        <Card className="p-4 text-sm text-destructive" role="alert">
          Couldn&apos;t load your report: {describeError(error)}
        </Card>
      )}

      {data && (
        <>
          <section className="space-y-2">
            {view === "daily" ? (
              daily.rows.length === 0 ? (
                <Card className="p-6 text-center text-sm text-muted-foreground">
                  No loads on {daily.dayLabel}.
                </Card>
              ) : (
                <ul className="space-y-2">
                  {daily.rows.map((row) => (
                    <li key={row.id}>
                      <DailyLoadCard row={row} />
                    </li>
                  ))}
                </ul>
              )
            ) : (
              <WeeklyDays weekly={weekly} />
            )}
          </section>

          {/* The number dispatch checks against the settlement. It is the
              loudest thing on the screen on purpose. */}
          <Card className="p-4 flex items-baseline justify-between gap-4">
            <div>
              <div className="text-sm font-medium">
                {view === "daily" ? "Day total" : "Week total"}
              </div>
              <div className="text-xs text-muted-foreground">
                {count} load{count === 1 ? "" : "s"}
                {needAmount > 0 && (
                  <span className="text-amber-600 dark:text-amber-500">
                    {" "}
                    · {needAmount} need{needAmount === 1 ? "s" : ""} an amount
                  </span>
                )}
              </div>
            </div>
            <div className="text-3xl font-bold tabular-nums">{totalLabel}</div>
          </Card>

          {billing.data?.allowed === false ? (
            // Sending is the gated action; the report itself stays readable
            // above this card, which is the point of gating actions rather
            // than screens.
            <PaywallCard status={billing.data} />
          ) : send.isSuccess ? (
            <SendResult result={send.data} onAgain={() => send.reset()} />
          ) : sendOpen ? (
            <SendForm
              scope={view === "daily" ? "day" : "week"}
              date={anchor}
              periodLabel={view === "daily" ? daily.dayLabel : weekly.rangeLabel}
              onCancel={() => {
                send.reset();
                setSendOpen(false);
              }}
              send={send}
            />
          ) : (
            <Button
              size="lg"
              className="w-full h-14 text-base gap-3"
              onClick={() => setSendOpen(true)}
            >
              <Send className="h-5 w-5" />
              Send to Dispatch
            </Button>
          )}

          {/* Below the manual send on purpose: this is the same action,
              set to happen on its own. Hidden behind the paywall with it,
              since arming a send an unentitled org cannot make would be a
              promise the server has to break. */}
          {billing.data?.allowed !== false && <WeeklyAutosendCard />}
        </>
      )}
    </div>
  );
}

// "Email my week to dispatch automatically."
//
// The one setting in this app that does something while nobody is looking,
// so it says plainly WHEN it fires — and since slice 17 that WHEN is the
// driver's to pick, day as well as hour. The copy is generated from what
// they chose rather than naming Monday and Tuesday, which are Evans' days
// and would be a lie on the next carrier the suite onboards.
//
// The address is the SAME dispatch_email the manual send remembers — there
// is one dispatcher, and two fields that could disagree would be a bug the
// driver only finds on a settlement week.
function WeeklyAutosendCard() {
  const { data: settingsData } = useSettings(true);
  const saved = settingsData?.settings;
  const save = useSaveSettings();

  // null means "untouched", so the saved value fills in when it arrives
  // without clobbering something already typed.
  const [emailDraft, setEmailDraft] = useState<string | null>(null);
  const email = emailDraft ?? saved?.dispatchEmail ?? "";
  const enabled = saved?.weeklyAutosendEnabled ?? false;
  const hour = saved?.weeklyAutosendHour ?? 8;
  // Monday, matching the column default, so the card reads the same as it
  // did before the day was a setting until the driver moves it.
  const dow = saved?.weeklyAutosendDow ?? 1;

  // Set when the driver flips the switch on with no address saved. The
  // server refuses that too (400); catching it here means they get told by
  // the field that needs filling rather than by an error line.
  const [needsEmail, setNeedsEmail] = useState(false);

  function toggle(next: boolean): void {
    if (!next) {
      setNeedsEmail(false);
      save.mutate({ weeklyAutosendEnabled: false });
      return;
    }
    const address = email.trim();
    if (address === "") {
      setNeedsEmail(true);
      return;
    }
    setNeedsEmail(false);
    // Sent together, so the toggle and the address it needs can never be
    // saved apart.
    save.mutate({ weeklyAutosendEnabled: true, dispatchEmail: address });
  }

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-start gap-3">
        <input
          id="weekly-autosend"
          type="checkbox"
          className="mt-1 h-5 w-5 shrink-0 accent-primary"
          checked={enabled}
          disabled={save.isPending}
          onChange={(e) => toggle(e.target.checked)}
        />
        <div className="space-y-1">
          <Label htmlFor="weekly-autosend" className="text-base font-medium leading-snug">
            Email my week to dispatch automatically.
          </Label>
          <p className="flex items-start gap-1.5 text-sm text-muted-foreground">
            <CalendarClock className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Sent {formatWeekday(dow)} ~{formatHour(hour)} {TERMINAL_TZ_LABEL} so dispatch can
              reconcile before payday.
            </span>
          </p>
        </div>
      </div>

      {(enabled || needsEmail) && (
        <div className="space-y-3 border-t pt-3">
          <div className="space-y-2">
            <Label htmlFor="autosend-email">Dispatch email</Label>
            <Input
              id="autosend-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="dispatch@example.com"
              value={email}
              onChange={(e) => setEmailDraft(e.target.value)}
              onBlur={() => {
                const address = email.trim();
                // Only when it actually changed — a blur on an untouched
                // field is not an edit worth a request.
                if (address === "" || address === (saved?.dispatchEmail ?? "")) return;
                save.mutate({ dispatchEmail: address });
              }}
            />
            {needsEmail && (
              <p className="text-sm text-destructive" role="alert">
                Add the dispatch email first — that&apos;s where the weekly report goes.
              </p>
            )}
          </div>

          {/* Day and hour together: they are one sentence — "Wednesday at
              8" — and reading them apart invites setting one and leaving
              the other where the last carrier left it. */}
          {enabled && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="autosend-day">Send on</Label>
                <select
                  id="autosend-day"
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs md:text-sm"
                  value={dow}
                  disabled={save.isPending}
                  onChange={(e) => save.mutate({ weeklyAutosendDow: Number(e.target.value) })}
                >
                  {SEND_DAYS.map((d) => (
                    <option key={d} value={d}>
                      {formatWeekday(d)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="autosend-hour">Send at</Label>
                <select
                  id="autosend-hour"
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs md:text-sm"
                  value={hour}
                  disabled={save.isPending}
                  onChange={(e) => save.mutate({ weeklyAutosendHour: Number(e.target.value) })}
                >
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {formatHour(h)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {save.isError && (
        <p className="text-sm text-destructive" role="alert">
          Couldn&apos;t save: {describeError(save.error)}
        </p>
      )}
    </Card>
  );
}

const HOURS = Array.from({ length: 24 }, (_, h) => h);

// The stored values are JS getUTCDay() (0 = Sunday), but the list reads
// Mon → Sun because that is the shape of the week the report covers, so
// Sunday's 0 sits at the END rather than at the top.
const SEND_DAYS = [1, 2, 3, 4, 5, 6, 0];

const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// 1 → "Monday". Indexed by the stored value directly, which is the point
// of storing the JS convention rather than a Monday-first one.
function formatWeekday(dow: number): string {
  return WEEKDAY_NAMES[dow] ?? "Monday";
}

// 0 → "12 AM", 8 → "8 AM", 13 → "1 PM". The stored value is a
// terminal-local hour; the driver reads it as a clock time.
function formatHour(hour: number): string {
  const suffix = hour < 12 ? "AM" : "PM";
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve} ${suffix}`;
}

// The send step, opened from the button. It asks for the one thing the
// server can't know — where to mail it — prefilled from the last send so
// the usual case is two taps. Everything ELSE about the report (the
// loads, the total) is re-derived server-side; this form deliberately
// posts only the window and the address.
function SendForm({
  scope,
  date,
  periodLabel,
  onCancel,
  send,
}: {
  scope: "day" | "week";
  date: string;
  periodLabel: string;
  onCancel: () => void;
  send: ReturnType<typeof useSendReport>;
}) {
  // Only fetched once this form is open — a driver who never sends never
  // spends a request on it.
  const { data: settingsData } = useSettings(true);
  const saved = settingsData?.settings;

  // null means "untouched", so the saved value can fill in when it
  // arrives without clobbering something already typed.
  const [emailDraft, setEmailDraft] = useState<string | null>(null);
  const [tractorDraft, setTractorDraft] = useState<string | null>(null);
  const email = emailDraft ?? saved?.dispatchEmail ?? "";
  const tractor = tractorDraft ?? saved?.tractor ?? "";

  return (
    <Card className="p-4 space-y-4">
      <div className="space-y-1">
        <h2 className="font-medium">Send to Dispatch</h2>
        <p className="text-sm text-muted-foreground">
          {scope === "week" ? `Week of ${periodLabel}` : periodLabel}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="dispatch-email">Dispatch email</Label>
        <Input
          id="dispatch-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="dispatch@example.com"
          value={email}
          onChange={(e) => setEmailDraft(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="dispatch-tractor">Tractor</Label>
        <Input
          id="dispatch-tractor"
          placeholder="Optional"
          value={tractor}
          onChange={(e) => setTractorDraft(e.target.value)}
        />
      </div>

      {send.isError && (
        isPaymentRequired(send.error) ? (
          // The trial can lapse between opening this form and submitting it.
          // The server's 402 is the authority, so the paywall replaces the
          // error line rather than reading as a failed send.
          <PaywallCard
            status={{ reason: paymentReason(send.error), manageable: false, configured: true }}
            compact
          />
        ) : (
          <p className="text-sm text-destructive" role="alert">
            Couldn&apos;t send: {describeError(send.error)}
          </p>
        )
      )}

      <div className="flex gap-2">
        <Button variant="outline" className="flex-1" onClick={onCancel} disabled={send.isPending}>
          Cancel
        </Button>
        <Button
          className="flex-1 gap-2"
          disabled={send.isPending || email.trim() === ""}
          onClick={() =>
            send.mutate({
              scope,
              date,
              dispatchEmail: email.trim(),
              tractor: tractor.trim() === "" ? null : tractor.trim(),
            })
          }
        >
          <Send className="h-4 w-4" />
          {send.isPending ? "Sending…" : "Send"}
        </Button>
      </div>
    </Card>
  );
}

// What the server actually did, quoted back. `delivered: false` is the
// no-email-key path — the report was composed and logged but never left
// the server, and saying "Sent" there would be a lie the driver only
// discovers when dispatch asks where the report is.
function SendResult({
  result,
  onAgain,
}: {
  result: SendReportResult;
  onAgain: () => void;
}) {
  return (
    <Card className="p-4 space-y-2" role="status">
      {result.delivered ? (
        <p className="text-sm font-medium">Sent to {result.to}</p>
      ) : (
        <p className="text-sm font-medium text-amber-600 dark:text-amber-500">
          Not sent — this server has no email key configured. The report for{" "}
          {result.to} was composed and logged only.
        </p>
      )}
      <p className="text-sm text-muted-foreground">
        {result.count} load{result.count === 1 ? "" : "s"} · {result.total}
        {result.needAmountCount > 0 && (
          <span className="text-amber-600 dark:text-amber-500">
            {" "}
            · {result.needAmountCount} without an amount
          </span>
        )}
      </p>
      <Button variant="outline" size="sm" onClick={onAgain}>
        Send again
      </Button>
    </Card>
  );
}

function PeriodTab({
  label,
  value,
  view,
  onSelect,
}: {
  label: string;
  value: View;
  view: View;
  onSelect: (v: View) => void;
}) {
  const selected = view === value;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={() => onSelect(value)}
      className={
        "rounded-md py-2 text-sm font-medium transition-colors " +
        (selected ? "bg-background shadow-sm" : "text-muted-foreground")
      }
    >
      {label}
    </button>
  );
}

// One load, as the paper ticket reads: container first (the driver's
// handle on it), then the ticket's identifying details, then the money.
function DailyLoadCard({ row }: { row: ReportLoadRow }) {
  return (
    <Link href={row.href}>
      <Card className="p-4 space-y-2 active:bg-accent transition-colors">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2 min-w-0 font-medium">
            <Container className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="truncate font-mono">{row.containerLabel}</span>
          </div>
          {row.needsAmount ? (
            <span className="shrink-0 text-sm font-medium text-amber-600 dark:text-amber-500">
              Needs amount
            </span>
          ) : (
            <span className="shrink-0 text-lg font-semibold tabular-nums">
              {row.amountLabel}
            </span>
          )}
        </div>

        <dl className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
          {row.moveTypeLabel && <Fact icon={Truck} value={row.moveTypeLabel} />}
          {row.carrierLabel && <Fact value={row.carrierLabel} />}
          {row.weightLabel && <Fact icon={Weight} value={row.weightLabel} />}
          {row.ticketLabel && <Fact label="Ticket" value={row.ticketLabel} />}
        </dl>
      </Card>
    </Link>
  );
}

function Fact({
  icon: Icon,
  label,
  value,
}: {
  icon?: typeof Truck;
  label?: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-1">
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {label && <dt className="sr-only">{label}</dt>}
      <dd>
        {label ? `${label} ${value}` : value}
      </dd>
    </div>
  );
}

// The week as dispatch reads it: one row per day, Monday → Sunday,
// including the days with no loads.
function WeeklyDays({ weekly }: { weekly: WeeklyLoadsReport }) {
  return (
    <ul className="space-y-2">
      {weekly.days.map((bucket) => (
        <li key={bucket.day}>
          <Card
            className={
              "p-4 flex items-center justify-between gap-4" +
              (bucket.count === 0 ? " text-muted-foreground" : "")
            }
          >
            <div className="min-w-0">
              <div className="font-medium">{bucket.dayLabel}</div>
              <div className="text-xs text-muted-foreground">
                {bucket.count === 0
                  ? "No loads"
                  : `${bucket.count} load${bucket.count === 1 ? "" : "s"}`}
                {bucket.needAmountCount > 0 && (
                  <span className="text-amber-600 dark:text-amber-500">
                    {" "}
                    · {bucket.needAmountCount} need
                    {bucket.needAmountCount === 1 ? "s" : ""} an amount
                  </span>
                )}
              </div>
            </div>
            <div className="text-base font-semibold tabular-nums">
              {bucket.subtotalLabel}
            </div>
          </Card>
        </li>
      ))}
    </ul>
  );
}
